import type {
  CancelProductionOrderRequest,
  CompleteProductionOperationRequest,
  CompleteProductionOrderRequest,
  CreateProductionBomRequest,
  CreateProductionOrderRequest,
  CreateProductionRoutingRequest,
  CreateProductionShiftRequest,
  CreateProductionWorkCentreRequest,
  IssueProductionMaterialRequest,
  PlanProductionOrderRequest,
  ProductionCommandResponse,
  ProductionOverviewResponse,
  ReceiveProductionOutputRequest,
  ReleaseProductionOrderRequest,
  ReverseProductionOutputRequest,
  ReturnProductionMaterialRequest,
  UpsertProductionCapacityDayRequest
} from '@fmcgbyalex/contracts/production';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren
} from 'react';
import { ApiError, FmcgApiClient, createIdempotencyKey } from '../lib/api-client';
import { useApplication } from './ApplicationProvider';

export type ProductionNotice = Readonly<{
  tone: 'success' | 'error' | 'info';
  title: string;
  detail: string;
}>;

export type ProductionContextValue = Readonly<{
  overview: ProductionOverviewResponse | null;
  loading: boolean;
  refreshing: boolean;
  busyAction: string | null;
  error: ApiError | null;
  notice: ProductionNotice | null;
  refresh: () => Promise<void>;
  dismissNotice: () => void;
  createWorkCentre: (input: CreateProductionWorkCentreRequest) => Promise<ProductionCommandResponse>;
  createShift: (workCentreId: string, input: CreateProductionShiftRequest) => Promise<ProductionCommandResponse>;
  upsertCapacityDay: (workCentreId: string, input: UpsertProductionCapacityDayRequest) => Promise<ProductionCommandResponse>;
  createBom: (input: CreateProductionBomRequest) => Promise<ProductionCommandResponse>;
  createRouting: (input: CreateProductionRoutingRequest) => Promise<ProductionCommandResponse>;
  createOrder: (input: CreateProductionOrderRequest) => Promise<ProductionCommandResponse>;
  planOrder: (orderId: string, input: PlanProductionOrderRequest) => Promise<ProductionCommandResponse>;
  releaseOrder: (orderId: string, input: ReleaseProductionOrderRequest) => Promise<ProductionCommandResponse>;
  issueMaterial: (orderId: string, input: IssueProductionMaterialRequest) => Promise<ProductionCommandResponse>;
  returnMaterial: (orderId: string, input: ReturnProductionMaterialRequest) => Promise<ProductionCommandResponse>;
  startOperation: (orderId: string, operationId: string) => Promise<ProductionCommandResponse>;
  completeOperation: (orderId: string, operationId: string, input: CompleteProductionOperationRequest) => Promise<ProductionCommandResponse>;
  receiveOutput: (orderId: string, input: ReceiveProductionOutputRequest) => Promise<ProductionCommandResponse>;
  reverseOutput: (orderId: string, outputId: string, input: ReverseProductionOutputRequest) => Promise<ProductionCommandResponse>;
  completeOrder: (orderId: string, input: CompleteProductionOrderRequest) => Promise<ProductionCommandResponse>;
  cancelOrder: (orderId: string, input: CancelProductionOrderRequest) => Promise<ProductionCommandResponse>;
}>;

const ProductionContext = createContext<ProductionContextValue | null>(null);

export function ProductionProvider({ children }: PropsWithChildren) {
  const application = useApplication();
  const [overview, setOverview] = useState<ProductionOverviewResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [busyAction, setBusyAction] = useState<string | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [notice, setNotice] = useState<ProductionNotice | null>(null);
  const generation = useRef(0);

  const client = useMemo(
    () => application.authentication
      ? new FmcgApiClient(application.runtime, application.authentication)
      : null,
    [application.authentication, application.runtime]
  );

  useEffect(() => {
    if (application.status !== 'ready' || !application.tenantId || !application.session || !client) {
      ++generation.current;
      setOverview(null);
      setLoading(false);
      setError(null);
      return;
    }
    void load(client, application.tenantId, true);
  }, [application.status, application.tenantId, application.session, client]);

  async function load(activeClient: FmcgApiClient, tenantId: string, initial: boolean): Promise<void> {
    const current = ++generation.current;
    if (initial) setLoading(true);
    else setRefreshing(true);
    setError(null);
    const moduleEnabled = application.session?.modules.some((module) => module.key === 'production' && module.enabled) ?? false;
    const readable = moduleEnabled && new Set(application.session?.permissions ?? []).has('production.reports.read');
    try {
      const next = readable
        ? await activeClient.get<ProductionOverviewResponse>('/v1/production/overview', tenantId)
        : null;
      if (current !== generation.current) return;
      setOverview(next);
    } catch (caught) {
      if (current !== generation.current) return;
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({ tone: 'error', title: apiError.problem.title, detail: apiError.problem.detail ?? 'Production data could not be loaded.' });
    } finally {
      if (current === generation.current) {
        setLoading(false);
        setRefreshing(false);
      }
    }
  }

  async function refresh(): Promise<void> {
    await load(requireClient(), requireTenant(), false);
  }

  async function mutate<T>(
    busy: string,
    successTitle: string,
    successDetail: string,
    operation: (activeClient: FmcgApiClient, tenantId: string) => Promise<T>
  ): Promise<T> {
    const activeClient = requireClient();
    const tenantId = requireTenant();
    setBusyAction(busy);
    setNotice(null);
    try {
      const result = await operation(activeClient, tenantId);
      await load(activeClient, tenantId, false);
      setNotice({ tone: 'success', title: successTitle, detail: successDetail });
      return result;
    } catch (caught) {
      const apiError = toApiError(caught);
      setError(apiError);
      setNotice({ tone: 'error', title: apiError.problem.title, detail: apiError.problem.detail ?? 'The manufacturing request failed.' });
      throw apiError;
    } finally {
      setBusyAction(null);
    }
  }

  function requireClient(): FmcgApiClient {
    if (!client) throw new Error('Authentication is required.');
    return client;
  }

  function requireTenant(): string {
    if (!application.tenantId) throw new Error('A tenant must be selected.');
    return application.tenantId;
  }

  const value: ProductionContextValue = {
    overview,
    loading,
    refreshing,
    busyAction,
    error,
    notice,
    refresh,
    dismissNotice: () => setNotice(null),
    createWorkCentre: (input) => mutate('Creating work centre', 'Work centre created', `${input.code} is ready for capacity planning.`, (api, tenantId) =>
      api.mutate('POST', '/v1/production/work-centres', input, tenantId, createIdempotencyKey('production-work-centre'))),
    createShift: (workCentreId, input) => mutate('Creating shift', 'Shift created', `${input.name} now contributes recurring capacity.`, (api, tenantId) =>
      api.mutate('POST', `/v1/production/work-centres/${encodeURIComponent(workCentreId)}/shifts`, input, tenantId, createIdempotencyKey('production-shift'))),
    upsertCapacityDay: (workCentreId, input) => mutate('Updating capacity', 'Capacity calendar updated', `${input.workDate} now has ${input.capacityMinutes} minutes.`, (api, tenantId) =>
      api.mutate('PUT', `/v1/production/work-centres/${encodeURIComponent(workCentreId)}/capacity-days`, input, tenantId, createIdempotencyKey('production-capacity-day'))),
    createBom: (input) => mutate('Creating bill of materials', 'BOM created', `${input.code} is available for production planning.`, (api, tenantId) =>
      api.mutate('POST', '/v1/production/boms', input, tenantId, createIdempotencyKey('production-bom'))),
    createRouting: (input) => mutate('Creating routing', 'Routing created', `${input.code} is available for finite scheduling.`, (api, tenantId) =>
      api.mutate('POST', '/v1/production/routings', input, tenantId, createIdempotencyKey('production-routing'))),
    createOrder: (input) => mutate('Creating production order', 'Production order created', 'The manufacturing demand is ready for planning and release.', (api, tenantId) =>
      api.mutate('POST', '/v1/production/orders', input, tenantId, createIdempotencyKey('production-order'))),
    planOrder: (orderId, input) => mutate('Planning production order', 'Production order planned', 'Priority and start date were updated.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/plan`, input, tenantId, createIdempotencyKey('production-plan'))),
    releaseOrder: (orderId, input) => mutate('Releasing production order', 'Production order released', 'BOM, routing, reservations and capacity are frozen for execution.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/release`, input, tenantId, createIdempotencyKey('production-release'))),
    issueMaterial: (orderId, input) => mutate('Issuing material', 'Material issued', 'The component lot and inventory ledger were updated together.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/material-issues`, input, tenantId, createIdempotencyKey('production-issue'))),
    returnMaterial: (orderId, input) => mutate('Returning material', 'Material returned', 'Unused component stock was returned to the inventory ledger.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/material-returns`, input, tenantId, createIdempotencyKey('production-return'))),
    startOperation: (orderId, operationId) => mutate('Starting operation', 'Operation started', 'Actual execution timing has begun.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/operations/${encodeURIComponent(operationId)}/start`, {}, tenantId, createIdempotencyKey('production-operation-start'))),
    completeOperation: (orderId, operationId, input) => mutate('Completing operation', 'Operation completed', 'Labour, machine time and actual conversion cost were captured.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/operations/${encodeURIComponent(operationId)}/complete`, input, tenantId, createIdempotencyKey('production-operation-complete'))),
    receiveOutput: (orderId, input) => mutate('Receiving finished output', 'Finished output received', 'The produced lot, inventory movement and genealogy were committed.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/outputs`, input, tenantId, createIdempotencyKey('production-output'))),
    reverseOutput: (orderId, outputId, input) => mutate('Reversing finished output', 'Output receipt reversed', 'The original receipt remains visible and a linked inventory reversal was posted.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/outputs/${encodeURIComponent(outputId)}/reversal`, input, tenantId, createIdempotencyKey('production-output-reversal'))),
    completeOrder: (orderId, input) => mutate('Completing production order', 'Production order completed', 'Execution history and remaining reservations are now final.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/complete`, input, tenantId, createIdempotencyKey('production-order-complete'))),
    cancelOrder: (orderId, input) => mutate('Cancelling production order', 'Production order cancelled', 'Unconsumed reservations and planned capacity were released.', (api, tenantId) =>
      api.mutate('POST', `/v1/production/orders/${encodeURIComponent(orderId)}/cancel`, input, tenantId, createIdempotencyKey('production-order-cancel')))
  };

  return <ProductionContext.Provider value={value}>{children}</ProductionContext.Provider>;
}

export function useProduction(): ProductionContextValue {
  const context = useContext(ProductionContext);
  if (!context) throw new Error('useProduction must be used within ProductionProvider.');
  return context;
}

function toApiError(caught: unknown): ApiError {
  if (caught instanceof ApiError) return caught;
  return new ApiError(0, {
    type: 'https://fmcgbyalex.com/problems/client-error',
    title: 'The production request failed',
    status: 0,
    detail: caught instanceof Error ? caught.message : 'An unexpected client error occurred.'
  }, null);
}
